import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Tooltip,
} from "@material-ui/core";
import {
  Restore,
  Visibility,
  History,
} from "@material-ui/icons";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
  },
  list: {
    maxHeight: "600px",
    overflow: "auto",
  },
  listItem: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  versionInfo: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  author: {
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
  },
  rollbackChip: {
    marginLeft: theme.spacing(1),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

const VersionHistory = ({ versions, onRestore, onPreview }) => {
  const classes = useStyles();

  const formatDate = (date) => {
    return format(new Date(date), "PPpp", { locale: es });
  };

  const formatRelativeTime = (date) => {
    const now = new Date();
    const versionDate = new Date(date);
    const diffInHours = Math.floor((now - versionDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) {
      return "Hace menos de 1 hora";
    } else if (diffInHours < 24) {
      return `Hace ${diffInHours} hora${diffInHours > 1 ? "s" : ""}`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `Hace ${diffInDays} día${diffInDays > 1 ? "s" : ""}`;
    }
  };

  if (!versions || versions.length === 0) {
    return (
      <Paper className={classes.root}>
        <Box className={classes.header}>
          <History />
          <Typography variant="h6">Historial de Versiones</Typography>
        </Box>
        <Box className={classes.emptyState}>
          <Typography variant="body1">
            No hay versiones publicadas aún
          </Typography>
          <Typography variant="body2">
            Las versiones se crearán automáticamente cuando publiques cambios
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <History />
        <Typography variant="h6">Historial de Versiones</Typography>
        <Chip
          label={`${versions.length} versión${versions.length > 1 ? "es" : ""}`}
          size="small"
          color="primary"
        />
      </Box>
      <List className={classes.list}>
        {versions.map((version, index) => (
          <React.Fragment key={version.id}>
            <ListItem className={classes.listItem}>
              <ListItemText
                primary={
                  <Box className={classes.versionInfo}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body1">
                        Versión {versions.length - index}
                      </Typography>
                      {version.isRollback && (
                        <Chip
                          label="Rollback"
                          size="small"
                          color="secondary"
                          className={classes.rollbackChip}
                        />
                      )}
                      {index === 0 && (
                        <Chip
                          label="Actual"
                          size="small"
                          color="primary"
                          className={classes.rollbackChip}
                        />
                      )}
                    </Box>
                    <Typography className={classes.timestamp}>
                      {formatDate(version.publishedAt)}
                    </Typography>
                    <Typography className={classes.author}>
                      Publicado por: {version.publishedBy || "Sistema"}
                    </Typography>
                  </Box>
                }
                secondary={formatRelativeTime(version.publishedAt)}
              />
              <ListItemSecondaryAction>
                <Box className={classes.actions}>
                  <Tooltip title="Vista previa">
                    <IconButton
                      edge="end"
                      aria-label="preview"
                      onClick={() => onPreview(version)}
                    >
                      <Visibility />
                    </IconButton>
                  </Tooltip>
                  {index !== 0 && (
                    <Tooltip title="Restaurar esta versión">
                      <IconButton
                        edge="end"
                        aria-label="restore"
                        onClick={() => onRestore(version.id)}
                      >
                        <Restore />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </ListItemSecondaryAction>
            </ListItem>
            {index < versions.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>
    </Paper>
  );
};

export default VersionHistory;
